import { AlertCircle, CheckCircle2, Upload, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ProgressBar } from '@/components/ui/progress-bar';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/utils/cn';

export type MemberImportIndicatorStatus =
  | 'importing'
  | 'success'
  | 'partial'
  | 'failed'
  | 'error';

interface MemberImportStatusIndicatorProps {
  status: MemberImportIndicatorStatus;
  label: string;
  openLabel: string;
  dismissLabel: string;
  progress?: { processed: number; total: number };
  progressLabel?: string;
  progressValueText?: string;
  onOpen: () => void;
  onDismiss: () => void;
}

const statusIcon = (status: MemberImportIndicatorStatus) => {
  if (status === 'importing') return <Spinner className="text-brand" />;
  if (status === 'success') {
    return <CheckCircle2 className="size-4 shrink-0 text-success-fg" aria-hidden />;
  }
  if (status === 'error' || status === 'failed') {
    return <AlertCircle className="size-4 shrink-0 text-danger-fg" aria-hidden />;
  }
  return <Upload className="size-4 shrink-0 text-brand" aria-hidden />;
};

export const MemberImportStatusIndicator = ({
  status,
  label,
  openLabel,
  dismissLabel,
  progress,
  progressLabel,
  progressValueText,
  onOpen,
  onDismiss,
}: MemberImportStatusIndicatorProps) => (
  <div
    className="fixed bottom-6 right-6 z-[60]"
    role="status"
    aria-live="polite"
    aria-busy={status === 'importing' || undefined}
  >
    <div
      className={cn(
        'w-[min(20rem,calc(100vw-3rem))] overflow-hidden rounded-2xl border border-border bg-surface shadow-e3',
        status === 'importing' ? 'p-3' : 'py-1 pl-3 pr-1',
      )}
    >
      <div className="flex items-center gap-2">
        {statusIcon(status)}
        <button
          type="button"
          onClick={onOpen}
          aria-label={openLabel}
          className="min-w-0 flex-1 truncate text-left text-body-sm font-medium text-fg hover:underline"
        >
          {label}
        </button>
        {status !== 'importing' ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7 shrink-0 text-fg-tertiary hover:text-fg"
            onClick={onDismiss}
            aria-label={dismissLabel}
          >
            <X className="size-4" aria-hidden />
          </Button>
        ) : null}
      </div>
      {status === 'importing' && progress && progressLabel && progressValueText ? (
        <div className="mt-3">
          <ProgressBar
            label={progressLabel}
            value={progress.processed}
            max={progress.total}
            valueText={progressValueText}
            size="sm"
          />
        </div>
      ) : null}
    </div>
  </div>
);
